import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './ExportDeck.css';

export default function ExportDeck() {
  useEffect(() => {
    document.title = 'Export and International Distribution | Nativeroots';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute('content', 'Premium cold-pressed sesame, groundnut and coconut oils from India. Explore export opportunities, product portfolio and distribution partnerships.');
    } else {
      const m = document.createElement('meta');
      m.name = 'description';
      m.content = 'Premium cold-pressed sesame, groundnut and coconut oils from India. Explore export opportunities, product portfolio and distribution partnerships.';
      document.head.appendChild(m);
    }
    window.scrollTo(0,0);
  }, []);

  const products = [
    { img: '/images/nut.jpeg', name: 'Cold-Pressed Groundnut Oil', size: '500ml', note: 'Clean flavour for cooking and retail.' },
    { img: '/images/sesame.jpeg', name: 'Cold-Pressed Sesame Oil', size: '500ml', note: 'Rich aroma, ideal for premium retail.' },
    { img: '/images/cocunut.jpeg', name: 'Cold-Pressed Coconut Oil', size: '500ml', note: 'Virgin quality for culinary and personal care.' },
    { img: '/images/vvvv.jpeg', name: 'Cold-Pressed Sesame Oil', size: '5L Tin', note: 'Export-ready bulk packaging for distributors.' },
  ];

  const reasons = [
    { title: 'Cold-Pressed Extraction', body: 'Traditional low-temperature extraction preserves aroma, flavour and nutrients.' },
    { title: 'No Chemical Refining', body: 'No chemical processing, bleaching or deodorization.' },
    { title: 'No Additives', body: 'Pure oil with no artificial additives or preservatives.' },
    { title: 'Export-Oriented Packaging', body: 'Packaging suitable for international distribution and retail channels.' },
    { title: 'Canadian Bilingual Label Capability', body: 'Labels can be adapted for English/French requirements.' },
    { title: 'Product of India', body: 'Manufactured in India using traditional oil extraction methods.' },
  ];

  return (
    <main className="export-deck page">

      <section className="export-hero">
        <div className="export-hero__inner">
          <div className="export-hero__copy">
            <h1>Export and Distribution</h1>
            <p className="export-hero__subtitle">Premium Cold-Pressed Oils from India for Global Retail Markets</p>
            <p className="export-hero__desc">Nativeroots® manufactures premium cold-pressed sesame, groundnut and coconut oils using traditional low-temperature extraction methods. We are actively seeking distribution and retail partnerships across international markets.</p>
            <div className="export-hero__ctas">
              <a href="/documents/Nativeroots_Export_Deck.pdf" download className="btn btn--gold">Download Export Deck</a>
              <Link to="/contact" className="btn btn--ghost">Contact Us</Link>
            </div>
          </div>
          <div className="export-hero__visual" aria-hidden>
            <div className="export-hero__card">
              <img src="/images/logo.jpeg" alt="Nativeroots logo" className="export-hero__logo" />
            </div>
          </div>
        </div>
      </section>

      <section className="product-portfolio section">
        <div className="section__hd">
          <span className="eyebrow">PRODUCT PORTFOLIO</span>
          <h2 className="h2">Export-Oriented Product Portfolio</h2>
          <p className="section__sub">Discover our range of premium cold-pressed oils developed for international retail, specialty food stores, distributors and importers.</p>
        </div>

        {/* Vertical brochure-style product listings */}
        <div className="product-list">
          {/* Product 1 — Groundnut */}
          <div className="export-product">
            <div className="export-product__img">
              <img src="/images/gn.jpeg" alt="Cold-Pressed Groundnut Oil" />
              <div className="badge badge--export">Export-Ready</div>
            </div>
            <div className="export-product__content">
              <h3>Cold-Pressed Groundnut Oil</h3>
              <p className="muted">Our cold-pressed groundnut oil is extracted using traditional low-temperature methods to preserve its natural flavour, aroma and nutritional profile. Suitable for international retail shelves, gourmet stores and health-conscious consumers seeking minimally processed edible oils.</p>
              <ul className="highlights">
                <li>Cold-Pressed Extraction</li>
                <li>No Chemical Refining</li>
                <li>No Additives</li>
                <li>Rich Natural Aroma</li>
                <li>Export-Ready Packaging</li>
              </ul>
              <div className="export-apps">
                <strong>Export Applications:</strong>
                <ul>
                  <li>Retail Distribution</li>
                  <li>Gourmet Stores</li>
                  <li>Health Food Markets</li>
                  <li>International Importers</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Product 2 — Sesame (alternate) */}
          <div className="export-product export-product--alt">
            <div className="export-product__content">
              <h3>Cold-Pressed Sesame Oil</h3>
              <p className="muted">Produced from carefully selected sesame seeds using traditional extraction methods. Designed for international markets that value authentic, unrefined oils with premium positioning and natural processing.</p>
              <div className="export-apps">
                <strong>Packaging:</strong> 500ml Retail Bottle
              </div>
              <ul className="highlights">
                <li>Traditional Extraction</li>
                <li>Pure Sesame Oil</li>
                <li>No Preservatives</li>
                <li>Premium Retail Positioning</li>
                <li>International Market Ready</li>
              </ul>
              <div className="export-apps">
                <strong>Export Applications:</strong>
                <ul>
                  <li>Retail Chains</li>
                  <li>Specialty Food Stores</li>
                  <li>Organic Product Markets</li>
                  <li>Ethnic Food Distributors</li>
                </ul>
              </div>
            </div>
            <div className="export-product__img">
              <img src="/images/ss.jpeg" alt="Cold-Pressed Sesame Oil" />
              <div className="badge badge--export">Export-Ready</div>
            </div>
          </div>

          {/* Product 3 — Coconut */}
          <div className="export-product">
            <div className="export-product__img">
              <img src="/images/cn2.jpeg" alt="Cold-Pressed Coconut Oil" />
              <div className="badge badge--export">Export-Ready</div>
            </div>
            <div className="export-product__content">
              <h3>Cold-Pressed Coconut Oil</h3>
              <p className="muted">A premium cold-pressed coconut oil retaining the natural characteristics of fresh coconut. Suitable for culinary, wellness and specialty retail markets worldwide.</p>
              <ul className="highlights">
                <li>Virgin Quality Available</li>
                <li>Naturally Processed</li>
                <li>No Chemical Treatment</li>
                <li>Export Packaging</li>
                <li>Product of India(Tiptur)</li>
              </ul>
              <div className="export-apps">
                <strong>Export Applications:</strong>
                <ul>
                  <li>Retail Distribution</li>
                  <li>Wellness Markets</li>
                  <li>Specialty Food Stores</li>
                  <li>International Distributors</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Product 4 — 5L Tin (alternate) */}
          <div className="export-product export-product--alt">
            <div className="export-product__content">
              <h3>Cold-Pressed Castor Oil</h3>
              <p className="muted">A versatile cold-pressed castor oil suitable for personal care, cosmetic and specialty industrial uses. Produced using low-temperature extraction to retain key fatty acids and purity required by wellness and personal care markets.</p>
              <div className="export-apps">
                <strong>Packaging:</strong> 250ml · 500ml · 1L
              </div>
              <ul className="highlights">
                <li>Cold-Pressed Extraction</li>
                <li>Personal Care Grade</li>
                <li>No Chemical Treatment</li>
                <li>Distributor Friendly</li>
                <li>Suitable for Formulation</li>
              </ul>
              <div className="export-apps">
                <strong>Export Applications:</strong>
                <ul>
                  <li>Importers</li>
                  <li>Distributors</li>
                  <li>Wholesale Channels</li>
                  <li>Personal Care & Wellness Retail</li>
                </ul>
              </div>
            </div>
            <div className="export-product__img">
              <img src="/images/castor.jpeg" alt="Cold-Pressed Castor Oil" />
              <div className="badge badge--export">Export-Ready</div>
            </div>
          </div>

        </div>
      </section>

      <section className="why-nativeroots section section--cream">
        <div className="section__hd">
          <span className="eyebrow">Why Nativeroots</span>
          <h2 className="h2">Export-Focused Advantages</h2>
        </div>
        <div className="why-grid">
          {reasons.map((r) => (
            <div key={r.title} className="why-card">
              <div className="why-card__icon">✓</div>
              <div>
                <h4>{r.title}</h4>
                <p className="muted">{r.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="export-readiness section">
        <div className="section__hd">
          <span className="eyebrow">Export Readiness</span>
          <h2 className="h2">Packaging & Markets</h2>
        </div>
        <div className="export-readiness__grid">
          <div className="export-readiness__col">
            <h3>Available Packaging</h3>
            <ul>
              <li>500ml Retail Bottles</li>
              <li>Bulk Packaging Available Upon Request</li>
            </ul>
            <h3 style={{marginTop:'1rem'}}>Distribution Opportunities</h3>
            <ul>
              <li>Distributor Partnerships</li>
              <li>Retail Partnerships</li>
              <li>Importer Collaborations</li>
            </ul>
          </div>
          <div className="export-readiness__col">
            <h3>Target Markets</h3>
            <ul>
              <li>Canada</li>
              <li>United Kingdom</li>
              <li>International Markets</li>
            </ul>
            <h3 style={{marginTop:'1rem'}}></h3>
          </div>
        </div>
      </section>
      

      <section className="download-deck section section--dark">
        <div className="download-deck__inner">
          <div className="download-deck__card">
            <h2>Download Export Deck</h2>
            <p className="muted">Access our export desk containing company overview, product portfolio, packaging options and partnership opportunities.</p>
            <a href="/documents/Nativeroots_Export_Deck.pdf" download className="btn btn--gold">Download Export Deck</a>
          </div>
        </div>
      </section>
      <section className="certifications section">
  <div className="section__hd">
    <span className="eyebrow">Business Verification</span>
    <h2 className="h2">Certifications & Business Documents</h2>
    <p className="section__sub">
      Download official business documents and certifications for supplier
      verification, distributor onboarding and partnership evaluation.
    </p>
  </div>

  <div className="certifications__grid">
    <div className="cert-card">
<div className="cert-card__icon">
  <img
    src="/images/fssai.jpeg"
    alt="FSSAI Logo"
    className="cert-logo"
  />
</div>      <h3>FSSAI License</h3>
      <p>
        Nativeroots operates under a valid FSSAI registration for food
        manufacturing and processing activities. Download the certificate
        for compliance review and business verification.
      </p>

      <a
        href="/documents/fssai.pdf"
        download
        className="btn btn--gold"
      >
        Download FSSAI Certificate
      </a>
    </div>

    <div className="cert-card">
<div className="cert-card__icon">
  <img
    src="/images/udhyam.webp"
    alt="Udyam Registration Logo"
    className="cert-logo"
  />
</div>     
 <h3>Udyam Registration</h3>
      <p>
        Nativeroots is registered under the Government of India Udyam
        Registration system for MSMEs. Download the certificate for supplier
        verification and partnership onboarding.
      </p>

      <a
        href="/documents/udhyam.pdf"
        download
        className="btn btn--gold"
      >
        Download Udyam Certificate
      </a>
    </div>
     <div className="cert-card">
  <div className="cert-card__icon">
    <img
      src="/images/iec.png"
      alt="Import Export Code Logo"
      className="cert-logo"
    />
  </div>

  <h3>Import Export Code (IEC)</h3>

  <p>
    Nativeroots holds a valid Import Export Code (IEC), enabling the company
    to legally engage in international trade and export activities. The IEC
    serves as an essential business identification number for global commerce,
    supporting export operations, distributor partnerships and international
    market expansion.
  </p>

  <a href="/documents/iec.pdf"
    download
    className="btn btn--gold"
  >
    Download IEC Certificate
  </a>
</div>
  </div>
 

  <div className="verification-note">
    <h3>Trusted Business Partner</h3>
    <p>
      Nativeroots welcomes distributor, importer and retail partnership
      enquiries. Additional business documents may be provided during
      onboarding and evaluation processes.
    </p>

    <div className="trust-badges">
      <span>✓ FSSAI Registered</span>
      <span>✓ Udyam Registered</span>
        <span>✓ IEC Certified</span>
      <span>✓ Product of India</span>
      <span>✓ Export-Oriented Packaging</span>
    </div>
  </div>
</section>

     
      <footer className="footer">
        <div className="footer__inner">
          <div className="footer__brand">
            <img src="/images/logo11.jpeg" alt="Nativeroots" className="footer__logo" />
            <div>
              <div className="footer__name">Nativeroots</div>
              <div className="footer__sub">S R Industries, Tumkur, Karnataka</div>
            </div>
          </div>
          <nav className="footer__nav">
            <Link to="/">Home</Link>
            <Link to="/products">Products</Link>
            <Link to="/contact">Contact</Link>
            <Link to="/contact">Export Enquiries</Link>
          </nav>
          <div className="footer__contact">
            <a href="tel:+918861432099">+91 88614 32099</a>
            <div>
              <a href="mailto:rakeshcsmail@gmail.com">rakeshcsmail@gmail.com</a>
              <br />
              <a href="mailto:rakesh@nativerootsheritage.co.in">rakesh@nativerootsheritage.co.in</a>
            </div>
            <a href="https://www.instagram.com/nativeroots_heritage" target="_blank" rel="noopener noreferrer">@nativeroots_heritage</a>
          </div>
        </div>
        <div className="footer__bar">© {new Date().getFullYear()} Nativeroots · S R Industries · All rights reserved</div>
      </footer>

    </main>
  );
}
